import { Skeleton, useToast } from '@chakra-ui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';
import { DataLake, Organization } from '@finley-technologies/finley-types';

import { useGetUserQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { errorToastConfig } from 'packages/common-frontend/constants';
import { ExploDashboardContainer } from 'packages/explo/ui/ExploDashboardContainer';
import { QuickSightContainer } from 'packages/quickSight/ui/QuickSightContainer';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

type PortfolioStratsContainerProps = {
  creditFacilityId: string;
};
export const PortfolioFacilitiesStratsContainer = ({
  creditFacilityId,
}: PortfolioStratsContainerProps) => {
  const flags = useFeatureFlags();
  const toast = useToast();
  const navigate = useNavigate();

  const { data: userData } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });

  if (!userData || !userData.user) {
    return (
      <Skeleton
        height="600px"
        data-testid="portfolio-strats-container-missing-user"
      />
    );
  }

  if (!flags) {
    return (
      <Skeleton
        height="600px"
        data-testid="portfolio-strats-container-missing-flag"
      />
    );
  }

  // Technically this should never hit as the route has already hidden this,
  // but just keeping as extra safety
  if (flags.showLenderFacilitiesStrats === 'hide') {
    navigate('/portfolio/overview/facilities');
    return <Skeleton height="600px" />;
  }

  if (flags.showLenderFacilitiesStrats === 'explo') {
    return (
      <ExploDashboardContainer
        organizationId={userData.user.organizationId}
        creditFacilityId={creditFacilityId}
        exploPlacement="lenderFacilitiesTab"
      />
    );
  }

  if (flags.showLenderFacilitiesStrats === 'quicksight') {
    return (
      <QuickSightContainer
        containerId="qs-portfolio-capital-provider-strats-dashboard"
        embedType={DataLake.QuicksightEmbedType.Dashboard}
        embedUrlQueryParams={{
          organizationId: userData.user.organizationId,
          organizationType: Organization.OrganizationType.CapitalProvider,
          dataType: DataLake.QuickSightDataType.ReceivableResponseTape,
          creditFacilityId,
          latest: true,
        }}
        embedStyling={{
          height: '704',
          resizeHeightOnSizeChangedEvent: true,
        }}
      />
    );
  }

  // This path should technically never hit
  navigate('/portfolio/overview/facilities');
  return <Skeleton height="600px" />;
};
